import { useEffect, useRef } from "react";

import type {
  CommanderHandDetailsView,
  CommanderPlacementTargetView,
} from "../presentation/game-view-model";
import { formatResources } from "../presentation/resource-format";

interface CommanderDeploymentPanelProps {
  commanderName: string;
  commander: CommanderHandDetailsView;
  target: CommanderPlacementTargetView;
  validationMessage: string | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CommanderDeploymentPanel({
  commanderName,
  commander,
  target,
  validationMessage,
  onConfirm,
  onCancel,
}: CommanderDeploymentPanelProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const replacing = target.actionType === "replace-commander";
  const verb = replacing ? "Replace" : "Deploy";
  const retainedArmySentence =
    target.armyCount === 0
      ? "The lane has no Armies to move."
      : target.armyCount === 1
        ? "The Army remains in this lane."
        : `All ${target.armyCount} Armies remain in this lane.`;

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (bodyRef.current !== null) bodyRef.current.scrollTop = 0;
      titleRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [commanderName, target.actionType, target.laneId]);

  return (
    <section
      className="commander-deployment"
      aria-labelledby="commander-deployment-title"
      data-action-type={target.actionType}
      data-testid="commander-deployment-panel"
    >
      <header className="commander-deployment__header">
        <div>
          <span className="eyebrow">Commander Deployment</span>
          <h2 id="commander-deployment-title" ref={titleRef} tabIndex={-1}>
            {replacing
              ? `Replace with ${commanderName}?`
              : `Deploy ${commanderName} to ${target.label} lane?`}
          </h2>
        </div>
        <div className="commander-deployment__badges" aria-label="Deployment consequences">
          <span>1 Deployment</span>
          {replacing ? <span>No refund</span> : null}
        </div>
      </header>

      <dl className="commander-deployment__summary" aria-label="Commander deployment summary">
        <div>
          <dt>Cost</dt>
          <dd>{formatResources(commander.cost)}</dd>
        </div>
        <div>
          <dt>Army Size</dt>
          <dd>
            {target.totalArmySize} / {commander.armyCapacity}
          </dd>
        </div>
        <div>
          <dt>Event Limit</dt>
          <dd>
            {target.eventLimitBefore} -&gt; {target.eventLimitAfter ?? target.eventLimitBefore}
          </dd>
        </div>
      </dl>

      <div className="commander-deployment__body" ref={bodyRef}>
        <dl className="commander-deployment__stats" aria-label={`${commanderName} printed values`}>
          <div>
            <dt>Attack</dt>
            <dd>{commander.attack}</dd>
          </div>
          <div>
            <dt>Guard</dt>
            <dd>{commander.guard}</dd>
          </div>
          <div>
            <dt>Capacity</dt>
            <dd>{commander.armyCapacity}</dd>
          </div>
        </dl>

        <p className="commander-deployment__consequence" data-testid="commander-consequence">
          {replacing ? (
            <>
              <strong>{target.currentCommander?.name ?? "The current Commander"}</strong> will enter
              your discard pile. {retainedArmySentence} The old Commander provides no refund.
            </>
          ) : target.armyCount > 0 ? (
            <>This Commander takes command of the existing ordered garrison.</>
          ) : (
            <>This Commander enters the empty Commander slot on your active Land.</>
          )}
        </p>

        <p className="commander-deployment__consequence">
          Pool after payment:{" "}
          <strong>{formatResources(target.kingdomPoolAfter ?? target.kingdomPoolBefore)}</strong>.
          Guard and Army Attack become active immediately, but
          <span data-testid="commander-attack-restriction">
            {" "}
            this Commander cannot attack this turn.
          </span>
        </p>

        {replacing || target.eventAuthorizationAlreadyUsed ? (
          <p className="commander-deployment__authorization">
            This lane&apos;s Event authorization will not refresh
            {target.eventAuthorizationAlreadyUsed ? "; its authorization is already used" : ""}.
          </p>
        ) : null}
      </div>

      {validationMessage ? (
        <p className="commander-deployment__validation" role="status">
          {validationMessage}
        </p>
      ) : null}

      <div className="commander-deployment__actions">
        <button className="action-button" type="button" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="action-button action-button--primary"
          type="button"
          data-testid="confirm-commander-placement"
          onClick={onConfirm}
        >
          {verb} Commander
        </button>
      </div>
    </section>
  );
}
